<script setup lang="ts">
import { computed, watch } from 'vue'
import Icon from '@/components/partials/Icon.vue'
import { useResizeObserver } from '@/composables/resizeObserver'
import { templateRef } from "@vueuse/core"

// Matches Google's AutocompletePrediction interface -> https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompletePrediction
const props = defineProps<{
  highlighted: boolean
  placeId: string
  structuredFormatting: google.maps.places.StructuredFormatting
}>()

const element = templateRef('element')

const match = computed<string>(() => props.structuredFormatting
    .main_text_matched_substrings
    .reduce((final, { offset, length }, index) => {
      const newLengthCorrection = final.length - props.structuredFormatting.main_text.length
      const chars = final.split('')

      chars.splice(offset + newLengthCorrection, 0, '<span class="prediction__matched-text">')
      chars.splice(offset + newLengthCorrection + length + 1, 0, '</span>')

      return chars.join('')
    }, props.structuredFormatting.main_text),
)

const { val } = useResizeObserver()

const secondaryText = computed<string>(() => val.value > 576
    ? props.structuredFormatting.secondary_text
    : props.structuredFormatting.secondary_text.split(',')[0],
)

watch(() => props.highlighted, (value) => {
  if (value) {
    element.value.focus()
  }
})
</script>

<template>
  <div
      ref="element"
      class="prediction"
      data-test-id="prediction"
      :data-place-id="placeId"
  >
    <Icon
        name="pin"
        class="prediction__pin"
    />
    <span class="prediction__text">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <span v-html="match"/>
      <span class="prediction__secondary-text">
        {{ secondaryText }}
      </span>
    </span>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/styling.scss";
@import "@/assets/css/mixins/breakpoints-up.scss";

.prediction {
  display:    flex;
  gap:        1rem;
  align-items: center;
  font-size:  1.25rem;
  padding:    0.5rem 1.5rem;
  border:     1px solid transparent;
  cursor:     pointer;
  user-select: none;
  transition: all 0.2s;

  &__text {
    width: 100%;

    @include sm-up {
      display: flex;
      align-items: center;
    }
  }

  &__matched-text {
    font-weight: 700;
  }

  &__secondary-text {
    font-size: 1rem;
    display:   block;
    margin-top: -0.5rem;

    @include sm-up {
      text-align: right;
      margin-left: auto;
      margin-top: 0;
    }
  }

  &__pin {
    color: var(--color-neutral-secondary);
  }

  &:hover,
  &:focus {
    // background-color: var(--color-brand-primary-10);
    background: $new-gradient-brand-main-10;
  }

  &:last-child {
    border-bottom-left-radius: 2.5rem;
    border-bottom-right-radius: 2.5rem;
    padding-bottom:            1.5rem;
  }
}
</style>
